import { Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { Form, Modal } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import TextInput from '../../../widgets/components/Input/TextInput'
import TextArea from '../../../widgets/components/Input/TextArea'
import { toast } from 'react-toastify'
import { createAiMemory, getAiMemory, updateAiMemory } from '../../../profile/core/_requests'
import { useAuth } from '../../../auth'
import * as Yup from 'yup'

const NewMemoryModal = ({
  isActive,
  setIsActive,
  aiMemoryFor,
  memoryId,
  setMemoryId,
  getAIMemories,
}: any) => {
  const { formatMessage } = useIntl()
  const { companyId, currentUser } = useAuth()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [memoryData, setMemoryData] = useState<any>()

  const initialValues = {
    memoryName: memoryData?.memoryName || '',
    memoryText: memoryData?.memoryText || '',
  }
  const memorySchema = Yup.object().shape({
    memoryName: Yup.string()
      .min(1, formatMessage({ id: 'Minimum 1 character is required' }))
      .max(50, formatMessage({ id: 'Maximum 50 characters' }))
      .required(formatMessage({ id: 'Memory Name is required' }))
      .nullable(),
    memoryText: Yup.string()
      .min(1, formatMessage({ id: 'Minimum 1 character is required' }))
      .required(formatMessage({ id: 'Memory Text is required' }))
      .nullable(),
  })

  const handleSubmit = async (values: any, resetForm: () => void) => {
    setIsLoading(true)
    try {
      const payload = {
        memoryName: values.memoryName,
        memoryText: values.memoryText,
        memoryFor: aiMemoryFor,
        entityId: aiMemoryFor === 'company' ? companyId : currentUser?.userId,
      }
      if (memoryId) {
        const {
          data: { success, errors },
        } = await updateAiMemory(payload, memoryId)
        if (success) {
          toast.success(formatMessage({ id: 'AI Memory Updated' }))
          setMemoryId(null)
          setIsActive(false)
          getAIMemories()
          resetForm()
        } else {
          errors?.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
        }
      } else {
        const {
          data: { success, errors },
        } = await createAiMemory(payload)
        if (success) {
          toast.success(formatMessage({ id: 'AI Memory Added' }))
          setMemoryId(null)
          setIsActive(false)
          getAIMemories()
          resetForm()
        } else {
          errors?.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
        }
      }
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }

  const getMemoryData = async () => {
    try {
      const {
        data: { success, data, errors },
      } = await getAiMemory(memoryId)
      if (success) {
        setMemoryData(data.aiMemory)
      } else {
        errors.forEach((error: string) => {
          toast.error(formatMessage({ id: error }))
        })
      }
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    if (memoryId) {
      getMemoryData()
    }
  }, [memoryId]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <Formik
        initialValues={initialValues}
        onSubmit={(values, { resetForm }) => handleSubmit(values, resetForm)}
        enableReinitialize={true}
        validationSchema={memorySchema}
        validateOnMount
      >
        {(formik) => (
          <Form className='w-100'>
            <Modal
              show={isActive}
              onHide={() => {
                setIsActive(false)
                formik.resetForm()
              }}
              size='lg'
            >
              <Modal.Header closeButton>
                <h2>{formatMessage({ id: memoryId ? 'Update Memory' : 'Add A Memory' })}</h2>
              </Modal.Header>
              <Modal.Body>
                <div className='container'>
                  <div className='w-100'>
                    <TextInput
                      fieldType='text'
                      label={formatMessage({ id: 'Memory Name' })}
                      fieldName={`memoryName`}
                      formik={formik}
                      placeholder={formatMessage({ id: 'Enter Memory Name' })}
                      toolTipText={formatMessage({
                        id: 'Give this memory a name to recognize it easily later.',
                      })}
                      isStarRequired={true}
                      isTooltipForModal={true}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  <div className='w-100'>
                    <TextArea
                      fieldName={'memoryText'}
                      formik={formik}
                      placeholder={formatMessage({
                        id: 'Enter Memory Text',
                      })}
                      label={formatMessage({ id: 'Memory Text' })}
                      toolTipText={formatMessage({
                        id: 'Please describe in as much detail as possible the memory that you want to feed to the AI. Please be descriptive and include all critical information required for the AI to generate accurate responses.',
                      })}
                      isStarRequired={true}
                      isTooltipForModal={true}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <div className='d-flex flex-md-row flex-column w-100 justify-content-end px-md-6 px-0 gap-3 mt-4'>
                  <button
                    type='button'
                    className='btn bg-light custom-button-property text-muted w-auto order-md-1 order-2'
                    onClick={() => {
                      setIsActive(false)
                      formik.resetForm()
                    }}
                  >
                    {formatMessage({ id: 'Cancel' })}
                  </button>

                  <button
                    type='submit'
                    className='btn text-white bg-primary custom-button-property w-auto order-md-2 order-1'
                    disabled={!formik.isValid || isLoading}
                    onClick={() => handleSubmit(formik.values, formik.resetForm)}
                  >
                    {!isLoading && (
                      <span className='indicator-label font-size-13 p-0'>
                        {formatMessage({ id: memoryId ? 'Update memory' : 'Save Memory' })}
                      </span>
                    )}
                    {isLoading && (
                      <span className='indicator-label font-size-13 p-0'>
                        {formatMessage({ id: 'Please wait...' })}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </Modal.Footer>
            </Modal>
          </Form>
        )}
      </Formik>
    </div>
  )
}
export default NewMemoryModal
